'use client';

import { Box, Container, Flex, Grid, Heading, Text } from '@chakra-ui/react';
import NextLink from 'next/link';
import { useState } from 'react';

const GREEN = '#1D9E75';
const GREEN_LIGHT = '#E1F5EE';

const FAQS = [
  {
    q: "Qu'est-ce que Fauconis ?",
    a: 'Fauconis est un cabinet de courtage indépendant spécialisé dans les mutuelles santé pour seniors, basé à Lyon. Contrairement aux comparateurs en ligne, nous vous proposons un accompagnement humain et personnalisé — un conseiller certifié se déplace chez vous dans le Rhône.',
  },
  {
    q: 'Le service de Fauconis est-il gratuit ?',
    a: "Oui, notre accompagnement est 100 % gratuit et sans engagement. Aucun frais de courtage n'est ajouté à votre contrat. Nous sommes rémunérés directement par les assureurs partenaires, sans impact sur votre tarif.",
  },
  {
    q: 'Fauconis est-il un assureur ?',
    a: 'Non. Fauconis est un courtier indépendant (ORIAS n°25006339), impartial vis-à-vis des assureurs. Notre rôle est de comparer les offres du marché et de vous conseiller la solution la plus adaptée à votre profil et budget.',
  },
  {
    q: 'Puis-je parler à un conseiller avant de souscrire ?',
    a: "Absolument — c'est même notre façon de travailler. Après votre demande de devis, Pol Vitry vous rappelle pour un échange de 5 min, puis se déplace à votre domicile pour une analyse complète.",
  },
  {
    q: 'Comment résilier ma mutuelle actuelle ?',
    a: 'Grâce à la loi résiliation infra-annuelle, vous pouvez changer de mutuelle à tout moment après un an de contrat, sans frais ni justification. Nous nous occupons de toutes les démarches de résiliation pour vous.',
  },
  {
    q: "Qu'est-ce que le 100 % Santé ?",
    a: 'Le 100 % Santé est un dispositif légal permettant le remboursement intégral de certaines lunettes, prothèses dentaires et appareils auditifs. En choisissant une mutuelle responsable avec équipements éligibles, votre reste à charge est nul.',
  },
  {
    q: 'Quels justificatifs faut-il pour souscrire ?',
    a: "Généralement : pièce d'identité, RIB, attestation de droits Sécurité sociale et un questionnaire de santé selon l'assureur. Nous vous guidons étape par étape dans la constitution de votre dossier.",
  },
  {
    q: 'Mes données personnelles sont-elles protégées ?',
    a: "Oui. Fauconis respecte scrupuleusement le RGPD. Vos informations ne sont jamais revendues à des tiers et ne sont transmises qu'aux assureurs que vous sélectionnez, avec votre accord explicite.",
  },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);

  return (
    <Box
      borderWidth="1.5px"
      borderColor={open ? GREEN : '#E5E7EB'}
      borderRadius="14px"
      bg="#F9FAFB"
      overflow="hidden"
      transition="border-color 0.2s"
    >
      <Flex
        as="button"
        w="full"
        justify="space-between"
        align="center"
        px="1.4rem"
        py="1.1rem"
        cursor="pointer"
        onClick={() => setOpen(!open)}
        textAlign="left"
        gap={4}
      >
        <Text fontSize="0.93rem" fontWeight="600">
          {q}
        </Text>
        <Box
          w="10px"
          h="10px"
          borderRight="2px solid"
          borderBottom="2px solid"
          borderColor="#6B7280"
          transform={open ? 'rotate(-135deg)' : 'rotate(45deg)'}
          transition="transform 0.25s"
          flexShrink={0}
          mt={open ? '4px' : '-4px'}
        />
      </Flex>
      {open && (
        <Box px="1.4rem" pb="1.1rem">
          <Text fontSize="0.88rem" color="#6B7280" lineHeight={1.7}>
            {a}
          </Text>
        </Box>
      )}
    </Box>
  );
}

export default function FaqSection() {
  return (
    <Box as="section" py={{ base: 12, lg: 20 }} px="5vw">
      <Container maxW="1200px" p={0}>
        <Grid templateColumns={{ base: '1fr', lg: '1fr 1.6fr' }} gap={{ base: 8, lg: '5rem' }} alignItems="start">
          <Box position={{ lg: 'sticky' }} top="navbar">
            <Flex
              w="64px"
              h="64px"
              borderRadius="full"
              bg={GREEN_LIGHT}
              align="center"
              justify="center"
              mb={5}
              color={GREEN}
              fontSize="2xl"
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </Flex>
            <Heading
              as="h2"
              fontSize={{ base: '1.5rem', lg: '2rem' }}
              fontWeight="800"
              letterSpacing="-0.03em"
              lineHeight={1.25}
              color="#111"
              mb={3}
            >
              Nos réponses à vos questions
            </Heading>
            <Text fontSize="0.95rem" color="#6B7280" lineHeight={1.7} mb={6}>
              Tout ce que vous devez savoir sur Fauconis.
            </Text>
            <NextLink
              href="/vos-questions"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                color: GREEN,
                fontWeight: '600',
                fontSize: '0.9rem',
                textDecoration: 'none',
              }}
            >
              Voir toutes les questions →
            </NextLink>
          </Box>

          <Flex direction="column" gap="0.75rem">
            {FAQS.map((faq) => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </Flex>
        </Grid>
      </Container>
    </Box>
  );
}
